/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { CONTENT_TYPE_JSON } from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";

/**
 * @fileoverview PaymentModal Component
 * @description Modal component for processing event registration payments.
 * This modal guides users through a multi-step payment flow that includes:
 * 1. Business and personal information collection
 * 2. Payment method selection (cash or card)
 * 3. Payment processing and confirmation
 *
 * Supports both cash payments via barcode generation and card payments
 * through OpenPay integration with comprehensive validation and error handling.
 *
 * @module modals/PaymentModal
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { createStoreCharge } from "../../logic/openpay/index.js";
import { ReactComponent as CloseIcon } from "../../assets/icons/PaymentModal/CloseIcon.svg";
import styles from "./index.module.css";
import { loadOpenPaySDK } from "../../utils/openpayClient.js";
import PaymentProgressBar from "../../components/PaymentProgressBar";
import PaymentModalStepRenderer from "./PaymentModalStepRenderer.jsx";
import PaymentModalFooterActions from "./steps/PaymentModalFooterActions.jsx";
import { 
  getCountryCodeOptions,
  getOperationEnvironmentOptions,
  getGenderOptions,
  getPositionOptions,
  getAreaOptions } from "../../data/selectOptions";
import {
  getInvoicePhoneDefaultsFromUser,
  getProfileFieldValue,
  getProfileByNumber,
  refreshToken,
  signOut,
  validateRFC,
} from "../../utils/common.js";
import { useFieldValidation } from "../../hooks/useFieldValidation";
import { useCardPaymentFlow } from "../../hooks/useCardPaymentFlow.js";
import {
  detectCardType,
  formatCardNumber,
  formatExpiryDate,
  validateExpiryDate,
} from "../../utils/payment/cardValidation.js";
import {
  isPaymentStep1Valid,
  isPaymentStep2Valid,
  isPaymentStep3Valid,
} from "../../utils/payment/paymentStepValidation.js";
import {
  buildEventAttendeePayload,
  registerEventAttendee,
} from "../../logic/payment/registerEventAttendee.js";
import { splitFullName } from "../../utils/payment/paymentFormatting.js";

/** @constant {string} CONTENT_TYPE_JSON - Standard JSON content type header value */

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * @typedef {Object} Event
 * @property {string} _id - Unique identifier for the event
 * @property {string} nombre - Event name
 * @property {number} [costo] - Default event cost
 * @property {number} [costoSocios] - Cost for member users
 * @property {number} [costoNoSocios] - Cost for non-member users
 * @property {boolean} [interesesComercialesRequerido] - Whether commercial interests field is required
 * @property {boolean} [expectativasRequerido] - Whether expectations field is required
 */

/**
 * @typedef {Object} User
 * @property {string} _id - Unique identifier for the user
 * @property {string} nombre - User's full name
 * @property {string} correoElectronico - User's email address
 * @property {string} tipoDeUsuario - User type ("1"-"5", where "4" is non-member)
 */

/**
 * @typedef {Object} RegistrationFormData
 * @property {string} razonSocial - Company legal name
 * @property {string} RFC - Tax ID (RFC)
 * @property {string} sector - Industry sector ID
 * @property {string} subSector - Industry subsector ID
 * @property {string} rama - Industry branch ID
 * @property {string} numeroTotalTrabajadores - Total number of employees
 * @property {string} ambitoOperaciones - Scope of operations
 * @property {string} nombre - Contact name
 * @property {string} codiogPais - Country code
 * @property {string} telefono - Phone number
 * @property {string} correoElectronico - Email address
 * @property {string} puesto - Job position
 * @property {string} area - Department/Area
 * @property {string} genero - Gender
 * @property {string} interesesComerciales - Commercial interests
 * @property {string} expectativas - Expectations
 * @property {string} nombreTitular - Cardholder name
 * @property {string} numeroTarjeta - Card number
 * @property {string} fechaDeVencimiento - Card expiry date
 * @property {string} codigoDeSeguridad - Card CVV
 */

/**
 * Modal component for processing event registration payments.
 * Provides a multi-step flow for collecting business information,
 * personal details, and processing payments via cash or card.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {Event} props.event - Event object containing event details and pricing
 * @param {User} props.user - Current user object with profile information
 *
 * @example
 * <PaymentModal
 *   open={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   event={selectedEvent}
 *   user={currentUser}
 * />
 *
 * @returns {React.ReactElement|null} The modal component or null if not open
 */
const PaymentModal = ({ open, onClose, event, user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const countryCodeOptions = getCountryCodeOptions(t);
  const [stepNumber, setStepNumber] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [cashPaymentLoading, setCashPaymentLoading] = useState(false);
  const [cashPaymentReference, setCashPaymentReference] = useState(null);
  const [cashPaymentAmount, setCashPaymentAmount] = useState(null);
  const [cashPaymentDueDate, setCashPaymentDueDate] = useState(null);
  const [cardPaymentLoading, setCardPaymentLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [popupWindow, setPopupWindow] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  // Ref to track if polling should continue (stops when modal closes or user navigates away)
  const shouldContinuePolling = useRef(true);
  const pollingTimeoutRef = useRef(null);
  const [rfcFormatError, setRfcFormatError] = useState(null);

  // Initialize validation hook for form fields
  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    nombre: "",
    telefono: "",
    correoElectronico: "",
    numeroTotalTrabajadores: "",
  });

  const [formData, setFormData] = useState({
    // Step 1: Company Information
    razonSocial: "",
    RFC: "",
    perfilSeleccionado: 0,
    sector: "",
    subSector: "",
    rama: "",
    numeroTotalTrabajadores: "",
    ambitoOperaciones: "",
    nombre: "",
    codiogPais: "",
    telefono: "",
    correoElectronico: "",
    puesto: "",
    area: "",
    genero: "",
    interesesComerciales: "",
    expectativas: "",

    // Step 2: Card fields if card payment method is selected
    nombreTitular: "",
    numeroTarjeta: "",
    fechaDeVencimiento: "",
    codigoDeSeguridad: "",
  });

  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isFormDisabled, setIsFormDisabled] = useState(false);

  const [cardType, setCardType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash"); // "cash" or "card"
  const [isCopied, setIsCopied] = useState(false);
  const [cardValidationError, setCardValidationError] = useState("");
  const [expiryValidationError, setExpiryValidationError] = useState("");
  const [hasRegistered, setHasRegistered] = useState(false);
  const [wantsInvoiceCash, setWantsInvoiceCash] = useState(false);
  const [phoneCountryCodeCash, setPhoneCountryCodeCash] = useState("");
  const [confirmPhoneCash, setConfirmPhoneCash] = useState("");
  const [wantsInvoiceCard, setWantsInvoiceCard] = useState(false);
  const [phoneCountryCodeCard, setPhoneCountryCodeCard] = useState("");
  const [confirmPhoneCard, setConfirmPhoneCard] = useState("");
  const operationEnvironmentOptions = getOperationEnvironmentOptions(t);
  const genderOptions = getGenderOptions(t);
  const positionOptions = getPositionOptions(t);
  const areaOptions = getAreaOptions(t);

  const profileOptions = [
    { value: 0, label: t("pages.companyProfile.profile1") },
    { value: 1, label: t("pages.companyProfile.profile2") },
    { value: 2, label: t("pages.companyProfile.profile3") },
  ];

  /**
   * Copies text to the system clipboard and shows a temporary success indicator.
   * @async
   * @param {string} text - The text to copy to clipboard
   * @returns {Promise<void>}
   */
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
      logger.info("Copied to clipboard:", { data: text });
    } catch (err) {
      logger.error("Failed to copy: ", { err: sanitizeError(err) });
    }
  };

  const getInvoicePhoneValidationArgs = () => ({
    wantsInvoiceCash,
    wantsInvoiceCard,
    phoneCountryCodeCash,
    phoneCountryCodeCard,
    confirmPhoneCash,
    confirmPhoneCard,
    getErrorMessage,
  });

  const isStep1Valid = () =>
    isPaymentStep1Valid({ formData, event, hasErrors });

  const isStep2Valid = () =>
    isPaymentStep2Valid({
      paymentMethod,
      formData,
      invoicePhoneValidationArgs: getInvoicePhoneValidationArgs(),
    });

  const isStep3Valid = () => isPaymentStep3Valid();

  /**
   * Handles Scope (Ámbito de Operación) change: updates root for profile 0,
   * or the matching profile in perfiles for profile 1/2 (by numeroDePerfil).
   * @param {Object} selectedOption - The selected react-select option (with .value)
   */
  const handleAmbitoOperacionesChange = (selectedOption) => {
    if (!selectedOption) {
      return;
    }
    const selectedProfileIndex = formData.perfilSeleccionado ?? 0;
    if (selectedProfileIndex === 0) {
      handleInputChange("ambitoOperaciones", selectedOption.value);
      return;
    }
    const profileNumber = selectedProfileIndex;
    const profile = getProfileByNumber(formData, profileNumber);
    if (profile && formData.perfiles) {
      setFormData((prev) => ({
        ...prev,
        perfiles: prev.perfiles.map((p) =>
          p?.numeroDePerfil === profileNumber
            ? { ...p, ambitoOperaciones: selectedOption.value }
            : p,
        ),
      }));
    } else {
      handleInputChange("ambitoOperaciones", selectedOption.value);
    }
  };

  /**
   * Handles form input changes with special formatting for card-related fields.
   * Applies formatting for card number and expiry date, and validates input in real-time.
   * @param {string} field - The form field name to update
   * @param {string} value - The new value for the field
   */
  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    if (field === "numeroTarjeta") {
      const formatted = formatCardNumber(value);
      setFormData((prev) => ({ ...prev, [field]: formatted }));
      const detectedType = detectCardType(value);
      setCardType(detectedType);

      // Set validation error if card number is long enough but no type detected
      const cardNumber = value.replace(/\s/g, "");
      if (cardNumber.length >= 13 && detectedType === "") {
        setCardValidationError(
          t("components.paymentModal.invalidCardNumberFormat"),
        );
      } else {
        setCardValidationError("");
      }
    } else if (field === "fechaDeVencimiento") {
      const formatted = formatExpiryDate(value);
      setFormData((prev) => ({ ...prev, [field]: formatted }));

      // Validate expiry date and set error
      const validation = validateExpiryDate(formatted);
      if (!validation.isValid && formatted.length === 5) {
        setExpiryValidationError(
          t("components.paymentModal.invalidExpiryDateFormat"),
        );
      } else {
        setExpiryValidationError("");
      }
    } else {
      // Other fields use the default setFormData above; no extra formatting needed
    }
  };

  const handleWantsInvoiceChange = (method, checked) => {
    const isCash = method === "cash";

    if (isCash) {
      setWantsInvoiceCash(checked);

      if (!checked) {
        setPhoneCountryCodeCash("");
        setConfirmPhoneCash("");
        handleFieldChange("confirmPhoneCash", "");
        return;
      }

      const { telefonoCodigoPais, telefono } =
        getInvoicePhoneDefaultsFromUser(user);

      setPhoneCountryCodeCash(
        (currentValue) => currentValue || telefonoCodigoPais,
      );
      setConfirmPhoneCash((currentValue) => currentValue || telefono);
      return;
    }

    setWantsInvoiceCard(checked);

    if (!checked) {
      setPhoneCountryCodeCard("");
      setConfirmPhoneCard("");
      handleFieldChange("confirmPhoneCard", "");
      return;
    }

    const { telefonoCodigoPais, telefono } =
      getInvoicePhoneDefaultsFromUser(user);

    setPhoneCountryCodeCard(
      (currentValue) => currentValue || telefonoCodigoPais,
    );
    setConfirmPhoneCard((currentValue) => currentValue || telefono);
  };

  const handleConfirmPhoneChange = (method, value) => {
    const validationField =
      method === "cash" ? "confirmPhoneCash" : "confirmPhoneCard";

    if (method === "cash") {
      setConfirmPhoneCash(value);
    } else {
      setConfirmPhoneCard(value);
    }

    handleFieldChange(validationField, value, {
      required: true,
      pattern: /^[\d\s]+$/,
      patternDescription: "PHONE_MUST_BE_10_DIGITS",
      customValidation: (phoneValue) => {
        if (!phoneValue || phoneValue.trim() === "") {
          return {
            isValid: false,
            error: "FIELD_REQUIRED",
          };
        }

        const phoneDigitsOnly = phoneValue.replace(/\s/g, "");
        if (!/^\d{10}$/.test(phoneDigitsOnly)) {
          return {
            isValid: false,
            error: "PHONE_MUST_BE_10_DIGITS",
          };
        }

        return { isValid: true };
      },
    });
  };

  /**
   * Gets the appropriate event cost based on the user's membership type.
   * Returns member pricing for user types 1, 2, 3, 5 and non-member pricing for type 4.
   * Falls back to default cost if specific pricing is not available.
   * @returns {number} The event cost in MXN
   */
  const getEventCost = () => {
    if (user?.tipoDeUsuario === "4") {
      return event.costoNoSocios || event.costo || 0;
    }
    if (["1", "2", "3", "5"].includes(user?.tipoDeUsuario)) {
      return event.costoSocios || event.costo || 0;
    }
    return 0; // Unknown user type — no event cost applies
  };

  /**
   * Handles cash/store payment processing.
   * Creates a store charge through OpenPay which generates a barcode for cash payment.
   * On success, moves to the next step to display the payment barcode.
   * @async
   * @returns {Promise<void>}
   */
  const handleCashPayment = async () => {
    if (!isStep2Valid()) {
      return;
    }

    setCashPaymentLoading(true);
    try {
      const { userName, userLastName } = splitFullName(user.nombre);

      const storeChargeData = {
        eventInformation: {
          eventId: event._id,
          eventName: event.nombre,
        },
        customerInformation: {
          userEmail: user.correoElectronico,
          userName: userName,
          userLastName: userLastName,
          quiereFactura: wantsInvoiceCash,
          telefonoCodigoPais: wantsInvoiceCash ? phoneCountryCodeCash : null,
          telefono: wantsInvoiceCash ? confirmPhoneCash : null,
        },
        paymentInformation: {
          amount: getEventCost(),
          description: "Store charge for event registration",
        },
      };

      const data = await createStoreCharge(user._id, storeChargeData, navigate);

      if (data) {
        logger.info("Store charge created successfully:", { data: data });
        setCashPaymentReference(data.storeChargeCreated.barcode.reference);
        setCashPaymentAmount(data.storeChargeCreated.amount);
        setCashPaymentDueDate(data.storeChargeCreated.dueDate);
        setStepNumber(stepNumber + 1);
      } else {
        logger.error("Failed to create store charge");
      }
    } catch (error) {
      logger.error("Error creating store charge:", {
        err: sanitizeError(error),
      });
    } finally {
      setCashPaymentLoading(false);
    }
  };

  const buildCardChargeData = useCallback(
    ({ tokenResponse, deviceSessionId, cardNumber }) => {
      const { userName, userLastName } = splitFullName(formData.nombreTitular);
      const redirectUrl = `${window.location.origin}/payment/3ds-callback`;

      return {
        eventInformation: {
          eventId: event._id,
          eventName: event.nombre,
        },
        customerInformation: {
          userEmail: user.correoElectronico,
          userName,
          userLastName,
          quiereFactura: wantsInvoiceCard,
          telefonoCodigoPais: wantsInvoiceCard ? phoneCountryCodeCard : null,
          telefono: wantsInvoiceCard ? confirmPhoneCard : null,
        },
        paymentInformation: {
          amount: getEventCost(),
          description: "Card charge for event registration",
          cardToken: tokenResponse.id,
          deviceSessionId,
          cardLastDigits: cardNumber.slice(-4),
          redirectUrl,
        },
      };
    },
    [
      confirmPhoneCard,
      event._id,
      event.nombre,
      formData.nombreTitular,
      phoneCountryCodeCard,
      user.correoElectronico,
      wantsInvoiceCard,
    ],
  );

  const { handleCardPayment } = useCardPaymentFlow({
    resultStepNumber: 4,
    stepNumber,
    setStepNumber,
    setPaymentStatus,
    setCardPaymentLoading,
    setModalMessage,
    setPopupWindow,
    setPollingAttempts,
    shouldContinuePolling,
    pollingTimeoutRef,
    navigate,
    t,
    user,
    formData,
    validateBeforePayment: isStep3Valid,
    buildCardChargeData,
  });


  /**
   * Retrieves the authentication token from localStorage.
   * @returns {string|undefined} The authentication token or undefined if not found
   */
  const getToken = () => {
    const token = localStorage.getItem("token");

    if (!!token && token !== "undefined") {
      return token;
    } else {
      logger.error(
        "Error trying to get the token to retrieve the users data. Token value:",
        { data: token },
      );
    }
  };

  /**
   * Registers the user as an event attendee.
   * Creates an attendee record after a successful card payment.
   * The modal stays open until the user closes it manually.
   * @async
   * @returns {Promise<void>}
   */
  const handleRegister = async () => {
    const attendanceData = buildEventAttendeePayload(formData, event);
    if (!attendanceData) {
      return;
    }

    const authToken = getToken();
    if (!authToken) {
      logger.error("Failed to get the token");
      return;
    }

    await registerEventAttendee({
      attendanceData,
      authToken,
      navigate,
    });
  };

  /**
   * Fetches all available industry sectors from the API.
   * Populates the sectors state for the sector dropdown.
   * @async
   * @returns {Promise<void>}
   */
  const getSectors = async () => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
      },
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}sector`,
        options,
      );
      const data = await response.json();
      logger.info("Sectors: ", { data: data });
      setSectors(data);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  /**
   * Fetches the user's existing registration information.
   * Pre-populates the form with previous registration data if available.
   * Handles token refresh automatically if the token has expired.
   * @async
   * @returns {Promise<void>}
   */
  const getRegistrationInfo = async () => {
    logger.info("getRegistrationInfo event: ", { data: event });
    const options = {
      method: "GET",
      headers: {
        "Content-Type": CONTENT_TYPE_JSON,
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ROOT_API}attendee/user/${localStorage.getItem(
          "userId",
        )}`,
        options,
      );
      const data = await response.json();
      logger.info("getRegistrationInfo data: ", { data: data });
      if (data?.message === "The incoming token has expired") {
        const newTokens = await refreshToken();
        if (!newTokens) {
          await signOut();
          navigate("/");
          return;
        }
        await getRegistrationInfo();
        return;
      }
      setFormData(data);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  useEffect(() => {
    if (open) {
      loadOpenPaySDK().catch((error) => {
        logger.error("Error loading OpenPay SDK:", {
          err: sanitizeError(error),
        });
      });
    }
  }, [open]);

  // Cleanup: Close popup and stop polling when modal closes
  useEffect(() => {
    if (!open) {
      // Stop polling
      shouldContinuePolling.current = false;
      // Clear any pending polling timeout
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      // Close popup if still open
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close();
      }
      setPopupWindow(null);
      setModalMessage(null);
    } else {
      // Reset polling flag when modal opens
      shouldContinuePolling.current = true;
    }
  }, [open, popupWindow]);

  // Stop polling when user navigates away from payment steps (step 3 or 4)
  useEffect(() => {
    // If user is not on step 3 or 4 (payment steps), stop polling
    if (stepNumber !== 3 && stepNumber !== 4) {
      shouldContinuePolling.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      setModalMessage(null);
    } else {
      // Reset polling flag when on payment steps
      shouldContinuePolling.current = true;
    }
  }, [stepNumber]);

  useEffect(() => {
    if (open && stepNumber === 1) {
      setSubsectors([]);
      setBranches([]);
      setPaymentMethod("cash");
      setCardValidationError("");
      setExpiryValidationError("");
      setHasRegistered(false);
      setModalMessage(null);
      setPollingAttempts(0);
      setWantsInvoiceCash(false);
      setPhoneCountryCodeCash("");
      setConfirmPhoneCash("");
      setWantsInvoiceCard(false);
      setPhoneCountryCodeCard("");
      setConfirmPhoneCard("");
      setRfcFormatError(null);
      handleFieldChange("confirmPhoneCash", "");
      handleFieldChange("confirmPhoneCard", "");

      // Check if user has existing registration info
      if (user && user?.tipoDeUsuario !== "4") {
        setIsFormDisabled(true);
        getRegistrationInfo();
      } else {
        // Reset form data when modal opens (only if user type is 4 or no user)
        setFormData((prev) => ({
          ...prev,
          razonSocial: "",
          RFC: "",
          perfilSeleccionado: 0,
          sector: "",
          subSector: "",
          rama: "",
          numeroTotalTrabajadores: "",
          ambitoOperaciones: "",
          nombre: "",
          codiogPais: "",
          telefono: "",
          correoElectronico: "",
          puesto: "",
          area: "",
          genero: "",
          interesesComerciales: "",
          expectativas: "",
          nombreTitular: "",
          numeroTarjeta: "",
          fechaDeVencimiento: "",
          codigoDeSeguridad: "",
        }));
      }

      // Always enable the form (like EventRegistrationModal)
      setIsFormDisabled(false);

      getSectors();
    }
  }, [open, stepNumber]);

  // Register user to event when payment is successful
  useEffect(() => {
    if (
      stepNumber === 4 &&
      paymentMethod === "card" &&
      paymentStatus === "PAYMENT_SUCCESS" &&
      !hasRegistered
    ) {
      setHasRegistered(true);
      handleRegister();
    }
  }, [stepNumber, paymentMethod, paymentStatus, hasRegistered]);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerTop}>
            {(stepNumber === 1 || stepNumber === 2) && (
              <PaymentProgressBar
                steps={[
                  { label: t("components.paymentModal.step1") },
                  { label: t("components.paymentModal.step2") },
                ]}
                currentStep={stepNumber}
              />
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          {stepNumber === 1 && (
            <div className={styles.modalTitle}>
              <div className={styles.title}>
                {t("components.paymentModal.title")}
              </div>
              <p className={styles.subtitle}>
                {t("components.paymentModal.subtitle")}
              </p>
            </div>
          )}
        </div>

        <PaymentModalStepRenderer
          stepNumber={stepNumber}
          paymentMethod={paymentMethod}
          paymentStatus={paymentStatus}
          registrationStepProps={{
            styles,
            t,
            event,
            formData,
            isFormDisabled,
            rfcFormatError,
            setRfcFormatError,
            handleInputChange,
            profileOptions,
            sectors,
            subSectors,
            branches,
            setFormData,
            setSubsectors,
            setBranches,
            operationEnvironmentOptions,
            handleAmbitoOperacionesChange,
            countryCodeOptions,
            handleFieldChange,
            getFieldValidationState,
            getErrorMessage,
            positionOptions,
            areaOptions,
            genderOptions,
          }}
          methodStepProps={{
            styles,
            t,
            paymentMethod,
            setPaymentMethod,
            wantsInvoiceCash,
            handleWantsInvoiceChange,
            countryCodeOptions,
            phoneCountryCodeCash,
            phoneCountryCodeCard,
            confirmPhoneCash,
            confirmPhoneCard,
            setPhoneCountryCodeCash,
            setPhoneCountryCodeCard,
            handleConfirmPhoneChange,
            getFieldValidationState,
            getErrorMessage,
            formData,
            handleInputChange,
            cardType,
            setCardType,
            cardValidationError,
            setCardValidationError,
            expiryValidationError,
            setExpiryValidationError,
            wantsInvoiceCard,
          }}
          cashConfirmStepProps={{
            styles,
            t,
            cashPaymentReference,
            cashPaymentAmount,
            cashPaymentDueDate,
            isCopied,
            copyToClipboard,
          }}
          cardConfirmStepProps={{
            styles,
            t,
            formData,
            user,
            getEventCost,
            modalMessage,
            cardPaymentLoading,
          }}
          successStepProps={{ styles, t, event, getEventCost }}
          errorStepProps={{ styles, t, event, getEventCost }}
        />

        <PaymentModalFooterActions
          styles={styles}
          t={t}
          stepNumber={stepNumber}
          setStepNumber={setStepNumber}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          paymentStatus={paymentStatus}
          formData={formData}
          setRfcFormatError={setRfcFormatError}
          isStep1Valid={isStep1Valid}
          isStep2Valid={isStep2Valid}
          isStep3Valid={isStep3Valid}
          onClose={onClose}
          cashPaymentLoading={cashPaymentLoading}
          handleCashPayment={handleCashPayment}
          cardPaymentLoading={cardPaymentLoading}
          handleCardPayment={handleCardPayment}
        />
      </div>
    </div>
  );
};

export default PaymentModal;
